import II18 from '../translate/II18';
import { gae } from './Analytics';

const isFirefoxBrowser = () => /Firefox|FxiOS/i.test(navigator.userAgent || '');
const isIOSDevice = () => /iPad|iPhone|iPod/i.test(navigator.userAgent || '');
const isEdgeBrowser = () => /Edg\//i.test(navigator.userAgent || '');

/**
 * Browser-specific steps to re-enable a blocked camera permission.
 */
export const getCameraPermissionHelpMessage = () => {
  if (window.isSafari && isIOSDevice()) {
    return II18.get('camera_disabled_help_safari_ios');
  }
  if (window.isSafari) {
    return II18.get('camera_disabled_help_safari');
  }
  if (isFirefoxBrowser()) {
    return II18.get('camera_disabled_help_firefox');
  }
  if (window.isChrome || isEdgeBrowser()) {
    return II18.get('camera_disabled_help_chrome');
  }
  return II18.get('camera_disabled_help_generic');
};

/**
 * Query Permissions API when available. Returns 'granted' | 'denied' | 'prompt' | null.
 */
export const queryCameraPermissionState = () => {
  if (!navigator.permissions || !navigator.permissions.query) {
    return Promise.resolve(null);
  }

  return navigator.permissions
    .query({ name: 'camera' })
    .then(status => status && status.state)
    .catch(() => null);
};

/**
 * Dialog when camera access was blocked: explains how to re-enable + optional Retry.
 *
 * @param {object} f7 - Framework7 app instance
 * @param {{ onRetry?: function, onClose?: function, analyticsCategory?: string }} options
 */
export const showCameraPermissionDeniedDialog = (f7, options = {}) => {
  const { onRetry, onClose, analyticsCategory = 'WEBCAMERA' } = options;

  gae(analyticsCategory, 'CAMERA_PERMISSION_HELP_SHOWN');

  const message = [
    II18.get('camera_disabled_message'),
    getCameraPermissionHelpMessage()
  ].join('<br/><br/>');

  const buttons = [
    {
      text: II18.get('title_close'),
      onClick: () => {
        if (onClose) onClose();
      }
    }
  ];

  if (onRetry) {
    buttons.push({
      text: II18.get('camera_permission_retry'),
      bold: true,
      cssClass: 'main-button',
      onClick: () => {
        gae(analyticsCategory, 'CAMERA_PERMISSION_RETRY');
        onRetry();
      }
    });
  }

  f7.dialog
    .create({
      title: II18.get('camera_disabled_title'),
      content: `<div style="text-align:left;padding:4px 2px;line-height:1.4">${message}</div>`,
      buttons,
      verticalButtons: !window.isDesktop
    })
    .open();
};

/**
 * Shared camera start error handling (permission vs hardware/other).
 *
 * @param {object} f7
 * @param {boolean} notAllowed
 * @param {{ onRetry?: function, onClose?: function, analyticsCategory?: string, errorMessageKey?: string }} options
 */
export const handleCameraStartError = (f7, notAllowed, options = {}) => {
  const {
    onRetry,
    onClose,
    analyticsCategory = 'WEBCAMERA',
    errorMessageKey = 'camera_error_message'
  } = options;

  gae(
    analyticsCategory,
    notAllowed ? 'ERROR_CAMERA_NO_PERMISSION' : 'ERROR_ON_START_CAMERA'
  );

  if (notAllowed) {
    showCameraPermissionDeniedDialog(f7, {
      onRetry,
      onClose,
      analyticsCategory
    });
    return;
  }

  f7.dialog.alert(II18.get(errorMessageKey), II18.get('common_error'), onClose);
};
